import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config';
import './AdminOrders.css';

// Resolve any prescription image path to a full displayable URL
const getPrescriptionFullUrl = (prescriptionData) => {
  if (!prescriptionData || prescriptionData.type !== 'image') return null;
  const url = prescriptionData.full_url || prescriptionData.image_url;
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return 'https://visioncareoptics.in/frontend/public' + url;
};

// Get payment method display text with hybrid payment support
const getPaymentMethodDisplay = (paymentMethod, paymentPercentage = 100) => {
  if (paymentMethod === 'cod') {
    return { text: 'Cash on Delivery', icon: '💰', class: 'method-cod' };
  } else if (paymentMethod === 'razorpay') {
    return { text: 'Razorpay (Full Online Payment)', icon: '💳', class: 'method-razorpay' };
  } else if (paymentMethod === 'half_online_half_cod') {
    return { text: `Half Online + Half COD (${paymentPercentage}% Advance)`, icon: '💸', class: 'method-hybrid' };
  }
  return { text: paymentMethod || 'Unknown', icon: '❓', class: 'method-unknown' };
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [generatingInvoice, setGeneratingInvoice] = useState(null);
  const [updatingCodPayment, setUpdatingCodPayment] = useState(null);

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/orders`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setOrders(data.data || []);
        else setError(data.message);
      } else {
        setError('Failed to load orders');
      }
    } catch (error) {
      setError('Error loading orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, orderStatus, paymentStatus) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify({ order_status: orderStatus, payment_status: paymentStatus })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setOrders(orders.map(o => o.id === orderId ? { ...o, order_status: orderStatus, payment_status: paymentStatus } : o));
          alert('Order updated successfully');
        } else { 
          alert(data.message || 'Failed to update order'); 
        }
      } else { 
        alert(`Failed to update order: ${response.status}`); 
      }
    } catch (error) {
      alert('Error updating order: ' + error.message);
    }
  };

  // NEW: Mark COD payment as collected for hybrid orders
  const markCodPaymentCollected = async (orderId) => {
    if (!window.confirm('Mark the remaining COD amount as collected? This will complete the payment for this order.')) {
      return;
    }
    
    setUpdatingCodPayment(orderId);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/orders/${orderId}/mark-cod-collected`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setOrders(orders.map(o => 
          o.id === orderId 
            ? { ...o, payment_status: 'completed', cod_paid_at: new Date().toISOString() } 
            : o
        ));
        alert('COD payment marked as collected successfully!');
      } else {
        alert(data.message || 'Failed to mark COD payment');
      }
    } catch (error) {
      alert('Error updating COD payment: ' + error.message);
    } finally {
      setUpdatingCodPayment(null);
    }
  };

  const generateInvoice = (order) => {
    setGeneratingInvoice(order.id);
    setTimeout(() => {
      const win = window.open('', '_blank');
      const hasPrescriptionImage = order.prescription_data?.type === 'image';
      const rxImageUrl = getPrescriptionFullUrl(order.prescription_data);
      const paymentMethodDisplay = getPaymentMethodDisplay(order.payment_method, order.payment_percentage);
      
      // Get payment status text for invoice
      const getPaymentStatusText = (status) => {
        const statusMap = {
          'pending': 'Pending',
          'completed': 'Completed',
          'failed': 'Failed',
          'refunded': 'Refunded',
          'advance_paid': 'Advance Paid'
        };
        return statusMap[status] || status;
      };

      // Calculate amounts for hybrid payment
      const advanceAmount = order.advance_amount || 0;
      const remainingAmount = order.remaining_amount || 0;
      const isHybridPayment = order.payment_method === 'half_online_half_cod';

      win.document.write(`<!DOCTYPE html><html><head><title>Invoice #${order.order_number}</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family:'Segoe UI',Arial,sans-serif; padding:40px; background:#f5f5f5; }
        .invoice-container { max-width:1000px; margin:0 auto; background:white; border-radius:12px; box-shadow:0 10px 40px rgba(0,0,0,0.1); overflow:hidden; }
        .invoice-header { background:linear-gradient(135deg,#1a1a1a,#2c3e50); color:white; padding:30px; display:flex; justify-content:space-between; align-items:center; }
        .logo-section h1 { font-size:28px; margin-bottom:5px; }
        .logo-section span { color:#d4af37; }
        .logo-section p { font-size:12px; opacity:0.8; }
        .invoice-title { text-align:right; }
        .invoice-title h2 { font-size:32px; margin-bottom:10px; }
        .invoice-body { padding:30px; }
        .company-details { display:flex; justify-content:space-between; margin-bottom:30px; padding-bottom:20px; border-bottom:2px solid #f0f0f0; }
        .company-info h3,.customer-info h3 { font-size:16px; margin-bottom:10px; color:#2c3e50; }
        .company-info p,.customer-info p { font-size:13px; color:#666; line-height:1.6; }
        .order-details { background:#f8f9fa; padding:15px; border-radius:8px; margin-bottom:30px; display:flex; justify-content:space-between; flex-wrap:wrap; gap:15px; }
        .order-detail-item { text-align:center; flex:1; min-width:120px; }
        .order-detail-item label { font-size:12px; color:#666; display:block; margin-bottom:5px; }
        .order-detail-item strong { font-size:14px; color:#2c3e50; }
        .payment-method-badge { display:inline-block; padding:4px 12px; border-radius:20px; font-size:12px; font-weight:600; }
        .method-cod { background:#e8f5e9; color:#27ae60; }
        .method-razorpay { background:#e3f2fd; color:#1976d2; }
        .method-hybrid { background:#fff3e0; color:#e67e22; }
        .products-table { width:100%; border-collapse:collapse; margin-bottom:30px; }
        .products-table th { background:#f8f9fa; padding:12px; text-align:left; font-size:13px; font-weight:600; color:#2c3e50; border-bottom:2px solid #e0e0e0; }
        .products-table td { padding:12px; border-bottom:1px solid #f0f0f0; font-size:13px; color:#555; }
        .summary-section { display:flex; justify-content:flex-end; margin-top:20px; }
        .summary-table { width:350px; border-collapse:collapse; }
        .summary-table td { padding:8px; font-size:13px; }
        .summary-table td:last-child { text-align:right; }
        .summary-table .total-row { font-weight:bold; font-size:16px; border-top:2px solid #e0e0e0; }
        .hybrid-payment-breakdown { background:#fff8f0; padding:15px; border-radius:8px; margin-top:15px; }
        .hybrid-payment-breakdown h4 { margin-bottom:10px; color:#e67e22; }
        .hybrid-breakdown-row { display:flex; justify-content:space-between; padding:8px 0; }
        .hybrid-breakdown-row.advance { color:#3498db; font-weight:600; }
        .hybrid-breakdown-row.remaining { color:#e67e22; font-weight:600; }
        .rx-section { margin-top:30px; padding:15px; background:#f8f9fa; border-radius:8px; }
        .rx-section h3 { font-size:14px; margin-bottom:10px; }
        .rx-img { max-width:200px; border-radius:6px; margin-top:8px; }
        .footer { background:#f8f9fa; padding:20px; text-align:center; font-size:12px; color:#666; }
        .print-btn { position:fixed; bottom:30px; right:30px; background:#3498db; color:white; border:none; padding:12px 24px; border-radius:50px; cursor:pointer; font-size:14px; font-weight:600; }
        @media print { body{background:white;padding:0;} .print-btn{display:none;} .invoice-container{box-shadow:none;} }
      </style></head><body>
      <div class="invoice-container">
        <div class="invoice-header">
          <div class="logo-section"><h1>VISION<span>CARE</span></h1><p>OPTICS</p></div>
          <div class="invoice-title"><h2>INVOICE</h2><p>#${order.order_number}</p></div>
        </div>
        <div class="invoice-body">
          <div class="company-details">
            <div class="company-info"><h3>VISION CARE OPTICS</h3><p>7/7 Balusamy Street, Opp to IOB,<br/>Palavanthangal, Chennai - 600114<br/>Phone: +91 94436 53501<br/>Email: kadharvco@gmail.com</p></div>
            <div class="customer-info"><h3>Bill To:</h3><p>${order.first_name} ${order.last_name}<br/>${order.email || 'N/A'}<br/>${order.shipping_address || 'N/A'}</p></div>
          </div>
          <div class="order-details">
            <div class="order-detail-item"><label>Order Date</label><strong>${new Date(order.created_at).toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'})}</strong></div>
            <div class="order-detail-item"><label>Order Status</label><strong>${(order.order_status||'').toUpperCase()}</strong></div>
            <div class="order-detail-item"><label>Payment Status</label><strong>${getPaymentStatusText(order.payment_status||'pending').toUpperCase()}</strong></div>
            <div class="order-detail-item"><label>Payment Method</label><strong><span class="payment-method-badge ${paymentMethodDisplay.class}">${paymentMethodDisplay.icon} ${paymentMethodDisplay.text}</span></strong></div>
          </div>
          <table class="products-table">
            <thead><tr><th>#</th><th>Product</th><th>Variant/Size</th><th>Lens</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr></thead>
            <tbody>
              ${(order.items||[]).map((item,idx) => `<tr>
                <td>${idx+1}</td>
                <td>${item.product_name}</td>
                <td>${[item.variant_name,item.size_name].filter(Boolean).join(' / ')||'-'}</td>
                <td>${item.lens_name||'-'}${item.lens_color_name?' ('+item.lens_color_name+')':''}</td>
                <td>${item.quantity}</td>
                <td>₹${parseFloat(item.price).toFixed(2)}</td>
                <td>₹${(item.price*item.quantity).toFixed(2)}</td>
              </tr>`).join('')}
            </tbody>
          </table>
          <div class="summary-section"><table class="summary-table">
            ${order.payment_details?`
              <tr><td>Subtotal:</td><td>₹${parseFloat(order.payment_details.subtotal||0).toFixed(2)}</td></tr>
              ${order.payment_details.discount_amount>0?`<tr><td>Discount:</td><td>-₹${parseFloat(order.payment_details.discount_amount).toFixed(2)}</td></tr>`:''}
              ${order.payment_details.coupon_code?`<tr><td>Coupon (${order.payment_details.coupon_code}):</td><td>-₹${parseFloat(order.payment_details.coupon_discount||0).toFixed(2)}</td></tr>`:''}
            `:''}
            <tr class="total-row"><td>Total Amount:</td><td>₹${parseFloat(order.total_amount).toFixed(2)}</td></tr>
           </table></div>
          
          ${isHybridPayment ? `
            <div class="hybrid-payment-breakdown">
              <h4>💰 Payment Breakdown (${paymentMethodDisplay.text})</h4>
              <div class="hybrid-breakdown-row advance">Advance Payment (Online): ₹${parseFloat(advanceAmount).toFixed(2)}</div>
              <div class="hybrid-breakdown-row remaining">Remaining (Cash on Delivery): ₹${parseFloat(remainingAmount).toFixed(2)}</div>
              ${order.cod_paid_at ? `<div class="hybrid-breakdown-row">✅ COD Collected on: ${new Date(order.cod_paid_at).toLocaleDateString()}</div>` : '<div class="hybrid-breakdown-row">⏳ Awaiting COD Collection</div>'}
            </div>
          ` : ''}
          
          ${hasPrescriptionImage && rxImageUrl ? `<div class="rx-section"><h3>Prescription (Uploaded Image)</h3><img src="${rxImageUrl}" alt="Prescription" class="rx-img"/></div>` : ''}
          ${order.prescription_data && !hasPrescriptionImage && order.prescription_data.right ? `
            <div class="rx-section"><h3>Prescription Details</h3><div style="display:flex;gap:20px;">
              <div><strong>Right Eye (OD)</strong><br/>SPH: ${order.prescription_data.right?.sph||'-'}<br/>CYL: ${order.prescription_data.right?.cyl||'-'}<br/>AXIS: ${order.prescription_data.right?.axis||'-'}<br/>ADD: ${order.prescription_data.right?.add||'-'}<br/>PD: ${order.prescription_data.right?.pd||'-'}</div>
              <div><strong>Left Eye (OS)</strong><br/>SPH: ${order.prescription_data.left?.sph||'-'}<br/>CYL: ${order.prescription_data.left?.cyl||'-'}<br/>AXIS: ${order.prescription_data.left?.axis||'-'}<br/>ADD: ${order.prescription_data.left?.add||'-'}<br/>PD: ${order.prescription_data.left?.pd||'-'}</div>
            </div></div>
          ` : ''}
        </div>
        <div class="footer"><p>Thank you for shopping with Vision Care Optics!</p><p>kadharvco@gmail.com | +91 94436 53501</p><p>Computer generated invoice. No signature required.</p></div>
      </div>
      <button class="print-btn" onclick="window.print()">🖨️ Print / Save as PDF</button>
      </body></html>`);
      win.document.close();
      setGeneratingInvoice(null);
    }, 100);
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '₹0.00';
    const n = typeof price === 'string' ? parseFloat(price) : price;
    return isNaN(n) ? '₹0.00' : `₹${n.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try { 
      return new Date(dateString).toLocaleDateString('en-IN', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getOrderStatusBadge = (status) => {
    const cfg = { 
      pending: '#f39c12', 
      processing: '#3498db', 
      shipped: '#9b59b6', 
      delivered: '#27ae60', 
      cancelled: '#e74c3c',
      confirmed: '#2ecc71'
    };
    const displayStatus = status === 'confirmed' ? 'Confirmed' : status;
    return <span className="status-badge" style={{ backgroundColor: cfg[status] || '#95a5a6' }}>{displayStatus}</span>;
  };

  const getPaymentStatusBadge = (status) => {
    const cfg = { 
      pending: '#f39c12', 
      completed: '#27ae60', 
      failed: '#e74c3c', 
      refunded: '#9b59b6',
      advance_paid: '#e67e22'
    };
    const displayText = status === 'advance_paid' ? 'Advance Paid' : (status || 'pending');
    return <span className="payment-status-badge" style={{ backgroundColor: cfg[status] || '#95a5a6' }}>{displayText}</span>;
  };

  // Payment method badge display with hybrid support
  const getPaymentMethodBadge = (method, paymentPercentage = 100) => {
    if (method === 'cod') {
      return <span className="payment-method-badge method-cod">💰 Cash on Delivery</span>;
    } else if (method === 'razorpay') {
      return <span className="payment-method-badge method-razorpay">💳 Razorpay (Full Online)</span>;
    } else if (method === 'half_online_half_cod') {
      return <span className="payment-method-badge method-hybrid">💸 Pay {paymentPercentage}% Now, {100 - paymentPercentage}% on Delivery</span>;
    }
    return <span className="payment-method-badge method-unknown">❓ {method || 'Unknown'}</span>;
  };

  const orderStatusOptions = ['pending','confirmed','processing','shipped','delivered','cancelled'].map(v => ({ 
    value: v, 
    label: v.charAt(0).toUpperCase() + v.slice(1) 
  }));
  
  const paymentStatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'advance_paid', label: 'Advance Paid' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' }
  ];

  const filteredOrders = orders.filter(order => {
    if (filter !== 'all' && order.order_status !== filter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return order.order_number?.toLowerCase().includes(s) || 
             order.first_name?.toLowerCase().includes(s) || 
             order.last_name?.toLowerCase().includes(s) || 
             order.email?.toLowerCase().includes(s);
    }
    return true;
  });

  const getTotalItems = (items) => items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

  if (loading) return <div className="admin-orders-loading">Loading orders...</div>;
  if (error) return <div className="admin-orders-error">{error}</div>;

  return (
    <div className="admin-orders">
      <div className="admin-orders-header">
        <h1>Orders Management</h1>
        <div className="admin-orders-filters">
          <div className="search-box">
            <input 
              type="text" 
              placeholder="Search by order #, customer name, email..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
            />
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="status-filter">
            <option value="all">All Orders</option>
            {orderStatusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <div className="admin-orders-stats">
        {[
          { label: 'Total Orders', val: orders.length },
          { label: 'Pending', val: orders.filter(o => o.order_status === 'pending').length },
          { label: 'Confirmed', val: orders.filter(o => o.order_status === 'confirmed').length },
          { label: 'Processing', val: orders.filter(o => o.order_status === 'processing').length },
          { label: 'Shipped', val: orders.filter(o => o.order_status === 'shipped').length },
          { label: 'Delivered', val: orders.filter(o => o.order_status === 'delivered').length },
          { label: 'Cancelled', val: orders.filter(o => o.order_status === 'cancelled').length },
          { label: 'Full Paid (Online)', val: orders.filter(o => o.payment_status === 'completed' && o.payment_method === 'razorpay').length },
          { label: 'Advance Paid', val: orders.filter(o => o.payment_status === 'advance_paid').length },
          { label: 'COD Pending', val: orders.filter(o => (o.payment_method === 'cod' || (o.payment_method === 'half_online_half_cod' && o.payment_status !== 'completed')) && o.order_status !== 'delivered').length },
        ].map(({ label, val }) => (
          <div key={label} className="stat-card">
            <span className="stat-label">{label}</span>
            <span className="stat-value">{val}</span>
          </div>
        ))}
      </div>

      <div className="admin-orders-list">
        {filteredOrders.length === 0 ? (
          <div className="no-orders">No orders found</div>
        ) : (
          filteredOrders.map(order => {
            const rxImageUrl = getPrescriptionFullUrl(order.prescription_data);
            const isImageRx = order.prescription_data?.type === 'image';
            const isManualRx = order.prescription_data && !isImageRx && (order.prescription_data.right || order.prescription_data.left);
            const isHybridPayment = order.payment_method === 'half_online_half_cod';
            const isCodPayment = order.payment_method === 'cod';
            const canCollectCod = isHybridPayment && order.payment_status === 'advance_paid' && order.order_status !== 'cancelled';
            const isCodCompleted = order.payment_status === 'completed';

            return (
              <div key={order.id} className="order-card">
                <div className="order-card-header">
                  <div className="order-info">
                    <span className="order-number">Order #{order.order_number}</span>
                    <span className="order-date">{formatDate(order.created_at)}</span>
                    <div className="order-customer">
                      <span className="customer-name">{order.first_name} {order.last_name}</span>
                      <span className="customer-email">{order.email}</span>
                    </div>
                  </div>
                  <div className="order-actions">
                    <div className="status-select-group">
                      <label>Order Status</label>
                      <select 
                        value={order.order_status} 
                        onChange={(e) => updateOrderStatus(order.id, e.target.value, order.payment_status)} 
                        className="status-select"
                      >
                        {orderStatusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>
                    <div className="status-select-group">
                      <label>Payment Status</label>
                      <select 
                        value={order.payment_status || 'pending'} 
                        onChange={(e) => updateOrderStatus(order.id, order.order_status, e.target.value)} 
                        className="payment-status-select"
                      >
                        {paymentStatusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>
                    <button className="view-details-btn" onClick={() => setSelectedOrder(selectedOrder === order.id ? null : order.id)}>
                      {selectedOrder === order.id ? 'Hide Details' : 'View Details'}
                    </button>
                  </div>
                </div>

                <div className="order-summary">
                  <div className="summary-item"><span>Items:</span><strong>{getTotalItems(order.items)}</strong></div>
                  <div className="summary-item"><span>Total:</span><strong className="total-amount">{formatPrice(order.total_amount)}</strong></div>
                  <div className="summary-item"><span>Payment:</span>{getPaymentMethodBadge(order.payment_method, order.payment_percentage)}</div>
                  <div className="summary-item"><span>Order:</span>{getOrderStatusBadge(order.order_status)}</div>
                  <div className="summary-item"><span>Payment Status:</span>{getPaymentStatusBadge(order.payment_status || 'pending')}</div>
                </div>

                {/* Hybrid Payment Quick Action */}
                {canCollectCod && (
                  <div className="hybrid-payment-alert">
                    <div className="alert-info">
                      <span className="alert-icon">💰</span>
                      <div className="alert-content">
                        <strong>Pending COD Collection: ₹{parseFloat(order.remaining_amount || 0).toFixed(2)}</strong>
                        <p>Advance of ₹{parseFloat(order.advance_amount || 0).toFixed(2)} already paid online. Collect remaining amount on delivery.</p>
                      </div>
                      <button 
                        className="collect-cod-btn" 
                        onClick={() => markCodPaymentCollected(order.id)}
                        disabled={updatingCodPayment === order.id}
                      >
                        {updatingCodPayment === order.id ? 'Processing...' : 'Mark COD Collected'}
                      </button>
                    </div>
                  </div>
                )}

                {selectedOrder === order.id && (
                  <div className="order-details">
                    {/* Products */}
                    <div className="details-section">
                      <div className="section-header">
                        <h3>Products</h3>
                        <button className="invoice-btn" onClick={() => generateInvoice(order)} disabled={generatingInvoice === order.id}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                          </svg>
                          {generatingInvoice === order.id ? 'Generating...' : 'Generate Invoice'}
                        </button>
                      </div>
                      <div className="products-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Variant</th>
                              <th>Size</th>
                              <th>Lens</th>
                              <th>Lens Color</th>
                              <th>Qty</th>
                              <th>Price</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(order.items || []).map((item, idx) => (
                              <tr key={idx}>
                                <td>
                                  <div className="product-info">
                                    <img 
                                      src={item.image?.startsWith('http') ? item.image : 'https://visioncareoptics.in/frontend/public' + (item.image || '')} 
                                      alt={item.product_name} 
                                      onError={(e) => e.target.src = '/assets/product-placeholder.jpg'} 
                                    />
                                    <span className="product-name">{item.product_name}</span>
                                  </div>
                                </td>
                                <td>{item.variant_name || '-'}</td>
                                <td>{item.size_name || '-'}</td>
                                <td>{item.lens_name || '-'}</td>
                                <td>
                                  {item.lens_color_name && (
                                    <span className="color-badge">
                                      <span className="color-dot" style={{ backgroundColor: item.lens_color_code }}></span>
                                      {item.lens_color_name}
                                    </span>
                                  )}
                                </td>
                                <td>{item.quantity}</td>
                                <td>{formatPrice(item.price)}</td>
                                <td className="item-total">{formatPrice(item.price * item.quantity)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Prescription — image type */}
                    {isImageRx && (
                      <div className="details-section prescription-section">
                        <h3>Prescription</h3>
                        <div className="prescription-image-section">
                          <p className="rx-label">📷 Customer uploaded a prescription image:</p>
                          {rxImageUrl ? (
                            <div className="rx-image-wrapper">
                              <img src={rxImageUrl} alt="Prescription" className="rx-admin-img" onError={(e) => { e.target.style.display='none'; }} />
                              <a href={rxImageUrl} target="_blank" rel="noopener noreferrer" className="rx-view-link">🔗 Open Full Image</a>
                            </div>
                          ) : (
                            <p className="rx-missing">Image URL not available</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Prescription — manual type */}
                    {isManualRx && (
                      <div className="details-section prescription-section">
                        <h3>Prescription Details</h3>
                        <div className="prescription-grid">
                          {['right', 'left'].map(eye => (
                            <div key={eye} className="prescription-card">
                              <h4>{eye === 'right' ? 'Right Eye (OD)' : 'Left Eye (OS)'}</h4>
                              <table className="prescription-table">
                                <tbody>
                                  {['sph','cyl','axis','add','pd'].map(field => (
                                    <tr key={field}>
                                      <td>{field.toUpperCase()}:</td>
                                      <td>{order.prescription_data[eye]?.[field] || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Shipping */}
                    <div className="details-section shipping-section">
                      <h3>Shipping Address</h3>
                      <div className="address-card">
                        <p>{order.shipping_address}</p>
                      </div>
                    </div>

                    {/* Payment Details - Enhanced with hybrid payment info */}
                    <div className="details-section payment-section">
                      <h3>Payment Details</h3>
                      <div className="payment-details-grid">
                        <div className="payment-item">
                          <span>Payment Method:</span>
                          <strong className={
                            order.payment_method === 'razorpay' ? 'razorpay-highlight' : 
                            order.payment_method === 'cod' ? 'cod-highlight' : 'hybrid-highlight'
                          }>
                            {order.payment_method === 'cod' && '💰 Cash on Delivery'}
                            {order.payment_method === 'razorpay' && '💳 Razorpay (Online Payment)'}
                            {order.payment_method === 'half_online_half_cod' && `💸 Pay ${order.payment_percentage || 50}% Now, ${100 - (order.payment_percentage || 50)}% on Delivery`}
                          </strong>
                        </div>
                        
                        <div className="payment-item">
                          <span>Payment Status:</span>
                          <strong>{getPaymentStatusBadge(order.payment_status || 'pending')}</strong>
                        </div>

                        {/* Hybrid Payment Breakdown */}
                        {isHybridPayment && (
                          <>
                            <div className="payment-item hybrid-breakdown">
                              <span>Advance Paid (Online):</span>
                              <strong className="advance-amount">₹{parseFloat(order.advance_amount || 0).toFixed(2)}</strong>
                            </div>
                            <div className="payment-item hybrid-breakdown">
                              <span>Remaining (COD):</span>
                              <strong className="remaining-amount">₹{parseFloat(order.remaining_amount || 0).toFixed(2)}</strong>
                            </div>
                            {order.cod_paid_at && (
                              <div className="payment-item success-note">
                                <span>✅ COD Collected:</span>
                                <strong>{new Date(order.cod_paid_at).toLocaleString()}</strong>
                              </div>
                            )}
                          </>
                        )}

                        {order.payment_method === 'razorpay' && order.payment_status === 'completed' && (
                          <div className="payment-item success-note">
                            <span>✅ Online Payment:</span>
                            <strong>Payment successfully completed via Razorpay</strong>
                          </div>
                        )}
                        
                        {order.payment_method === 'cod' && order.payment_status === 'pending' && (
                          <div className="payment-item info-note">
                            <span>ℹ️ COD Order:</span>
                            <strong>Customer will pay upon delivery</strong>
                          </div>
                        )}

                        {order.payment_details && (
                          <>
                            <div className="payment-item">
                              <span>Subtotal:</span>
                              <strong>{formatPrice(order.payment_details.subtotal)}</strong>
                            </div>
                            {order.payment_details.discount_amount > 0 && (
                              <div className="payment-item discount">
                                <span>Discount:</span>
                                <strong>-{formatPrice(order.payment_details.discount_amount)}</strong>
                              </div>
                            )}
                            {order.payment_details.coupon_code && (
                              <div className="payment-item discount">
                                <span>Coupon ({order.payment_details.coupon_code}):</span>
                                <strong>-{formatPrice(order.payment_details.coupon_discount)}</strong>
                              </div>
                            )}
                            <div className="payment-item total">
                              <span>Total:</span>
                              <strong>{formatPrice(order.total_amount)}</strong>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Order Timeline */}
                    <div className="details-section timeline-section">
                      <h3>Order Timeline</h3>
                      <div className="timeline">
                        <div className="timeline-item">
                          <div className="timeline-date">{formatDate(order.created_at)}</div>
                          <div className="timeline-status">Order Created</div>
                        </div>
                        {order.paid_at && (
                          <div className="timeline-item">
                            <div className="timeline-date">{formatDate(order.paid_at)}</div>
                            <div className="timeline-status">Advance Payment Received</div>
                          </div>
                        )}
                        {order.cod_paid_at && (
                          <div className="timeline-item">
                            <div className="timeline-date">{formatDate(order.cod_paid_at)}</div>
                            <div className="timeline-status">COD Payment Collected</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AdminOrders;